import { SHEET_KEYS } from '../../models/sheet-keys';
import { DEI } from '../../constants/qnames';
import { FilingFormState } from './filing-form-state.service';
import { SheetValidationContext, SheetValidator } from './sheet-validator';

/**
 * Pure, Angular-free assembler for {@link SheetValidationContext}.
 *
 * This is the single seam that keeps the headless listing path
 * (ConversionValidatorService) and the mounted role components from drifting:
 * both build their validators' context here, so a validator sees an
 * identically-shaped `crossSheetData` / `submissionType` / `accountingStandard`
 * no matter which path invoked it.
 *
 * No Angular DI, no service reads — every input arrives as the plain
 * `sheetDataByStateKey` map. That is deliberately what lets the pure validators
 * (and this builder) stay unit-testable without TestBed.
 *
 * Extracted verbatim from ConversionValidatorService.buildContext /
 * resolveActiveFilingInfo; that service now delegates here.
 */

const FILING_INFO_SUBMISSION_TYPE_KEY = DEI.TypeOfSubmission;
const FILING_INFO_ACCOUNTING_STANDARD_KEY =
  DEI.BasisOfAccountingStandardsAppliedToPrepareFinancialStatements;

/**
 * True when the slice carries the `ssmt-dei:TypeOfSubmission` KEY *and* holds at
 * least one non-empty value of its own.
 *
 * The key-presence half is the discriminator this function's doc comment has
 * always specified. The "has real content" half rejects a literally all-empty
 * slice, so an empty slot can never out-rank the one holding the filing's data.
 *
 * What it does NOT do — and the earlier comment here overstated: it does not
 * filter out a *backfilled* slot. `applyDefaultValuesToState`
 * (`filing-form-state.service.ts`) force-writes ~10 non-empty readonly defaults
 * (`StatusOfCompany`, `TaxonomyVersion`, `DescriptionOfPresentationCurrency`, …)
 * into every key already PRESENT on `filingInfo` / `scopeOfFiling`, so a
 * backfilled slot arrives here already carrying real values and passes this
 * predicate. Treat this as "not empty", not as "genuinely the active sheet".
 */
function isRealFilingInfoSlice(
  slice: Record<string, any> | undefined
): slice is Record<string, any> {
  if (!slice || !(FILING_INFO_SUBMISSION_TYPE_KEY in slice)) return false;
  return Object.values(slice).some(
    (value) => value !== null && value !== undefined && value !== ''
  );
}

/**
 * Resolve the slice that holds the real filing-info content for this
 * conversion, regardless of phase.
 *
 * The discriminator is the presence of `ssmt-dei:TypeOfSubmission`: the real
 * filing-info sheet always carries it (Phase 1 → role 0 / filingPrerequisites,
 * Phase 2 → role 1 / filingInfo), while the inactive slot is either absent or
 * the defaults stub `applyDefaultValuesToState` force-writes — and that stub
 * never contains `TypeOfSubmission`. `filingInfo` is checked first so a Phase 2
 * conversion resolves to role 1 even if both slots carry a TypeOfSubmission
 * (mid-migration payloads). Returns `{}` when neither slot has real content;
 * validators tolerate that absence.
 *
 * **Two passes, in this order — the order is load-bearing:**
 *
 *  1. **Non-empty `TypeOfSubmission`** (the historical test). Kept first so the
 *     resolution for every payload that has a submission type anywhere is
 *     byte-for-byte what it always was.
 *  2. **Key presence + non-empty slice** (the documented contract). A pure
 *     FALLBACK: it is reached only when NO slot carries a truthy submission
 *     type — which is what a cleared Basis-of-accounting-standards dropdown
 *     (`[showClear]`) can produce, since the derived `TypeOfSubmission` goes with
 *     it. Without this pass such a slice loses to the other slot and every
 *     phase-aware validator (roles 8/9/10/11/19) is handed the wrong one.
 *
 * A pure key-presence discriminator (pass 2 only) was rejected: it would let an
 * empty `filingInfo` slot out-rank a populated `filingPrerequisites` slice on a
 * Phase-1 conversion. Both orderings are pinned by spec.
 *
 * Scope note, so the guarantee is not read as stronger than it is: pass 2's
 * "non-empty" test only screens out an ALL-EMPTY slice. It cannot distinguish a
 * DTO-completeness-backfilled slot from a real one, because
 * `applyDefaultValuesToState` force-writes non-empty readonly defaults into
 * every present key before the payload ever reaches this function (see
 * `isRealFilingInfoSlice` above). Within pass 2, `filingInfo` first is what
 * decides that case.
 */
export function resolveActiveFilingInfo(
  sheetDataByStateKey: ReadonlyMap<keyof FilingFormState, Record<string, any>>
): Record<string, any> {
  const filingInfo = sheetDataByStateKey.get(SHEET_KEYS.filingInfo);
  const filingPrerequisites = sheetDataByStateKey.get(SHEET_KEYS.filingPrerequisites);

  // Pass 1 — a slot with an actual submission type wins.
  if (filingInfo?.[FILING_INFO_SUBMISSION_TYPE_KEY]) return filingInfo;
  if (filingPrerequisites?.[FILING_INFO_SUBMISSION_TYPE_KEY]) {
    return filingPrerequisites;
  }

  // Pass 2 — nobody has a value (e.g. the user cleared Basis). Fall back to the
  // documented discriminator so a real-but-cleared slice still beats a stub.
  if (isRealFilingInfoSlice(filingInfo)) return filingInfo;
  if (isRealFilingInfoSlice(filingPrerequisites)) return filingPrerequisites;

  return filingInfo ?? filingPrerequisites ?? {};
}

/**
 * Assemble the {@link SheetValidationContext} for a conversion's loaded payload.
 *
 * The active filing-info slice is aliased under BOTH filing-info keys in
 * `crossSheetData` so phase-aware, visibility-resolving validators
 * (role 8/9/10/11/19) that hard-code `crossSheetData.get('filingPrerequisites')`
 * stay lossless regardless of phase.
 */
export function buildSheetValidationContext(
  sheetDataByStateKey: ReadonlyMap<keyof FilingFormState, Record<string, any>>,
  opts?: {
    /**
     * Pre-resolved active filing-info slice. When supplied, it is used verbatim
     * instead of {@link resolveActiveFilingInfo}. The mounted/state-service path
     * passes its phase-signal resolution (`getActiveFilingInfoData()`) here so it
     * keeps that authoritative source; the headless path omits it and falls back
     * to the TypeOfSubmission discriminator (no phase signal available there).
     */
    activeFilingInfo?: Record<string, any>;
    /**
     * The immutable N8N-extracted baseline for the sheet being validated. When
     * supplied, calc-diff validators surface the non-blocking "Validated*"
     * (`hasSuggestedChanges`) asterisk by diffing recomputed totals against it;
     * without it they fall back to the persisted slice (recomputed == persisted)
     * and never flag the asterisk — mirroring what the headless orchestrator's
     * runRegistry threads in per sheet.
     */
    extractedBaseline?: Record<string, any>;
    /**
     * The N8N-extracted baselines for ALL sheets (headless `snapshotByStateKey`
     * / the mounted state service's snapshot store). Threaded onto the context so
     * cross-sheet recompute can anchor referenced operands to their baselines —
     * see {@link SheetValidationContext.crossSheetBaseline}. Optional: absent
     * paths fall back to recompute-with-inheritance.
     */
    crossSheetBaseline?: ReadonlyMap<keyof FilingFormState, Record<string, any>>;
  }
): SheetValidationContext {
  const filingInfo = opts?.activeFilingInfo ?? resolveActiveFilingInfo(sheetDataByStateKey);

  const crossSheetData = new Map(sheetDataByStateKey);
  crossSheetData.set(SHEET_KEYS.filingPrerequisites, filingInfo);
  crossSheetData.set(SHEET_KEYS.filingInfo, filingInfo);

  return {
    submissionType: (filingInfo[FILING_INFO_SUBMISSION_TYPE_KEY] as string) ?? '',
    accountingStandard: (filingInfo[FILING_INFO_ACCOUNTING_STANDARD_KEY] as string) ?? '',
    crossSheetData,
    extractedBaseline: opts?.extractedBaseline,
    crossSheetBaseline: opts?.crossSheetBaseline,
  };
}

/**
 * Sheets whose blocking verdict (`hasErrors`) is published from their registered
 * pure validator instead of the editor component's display-derived boolean — the
 * "single source of truth" allow-list. {@link pureVerdictForAllowListedSheet}
 * (called from FilingFormStateService.updateSheetLevelStatus) consults this; the
 * component's own boolean is kept for sheets not yet listed, so the convergence
 * rolls out one sheet at a time, each added only after it is verified at parity.
 *
 * Role 8 (Statement of Financial Position) was the pilot; role 12 (Statement of
 * Profit or Loss) followed. Role 23 (Notes — Issued Capital) and role 24
 * (Notes — Related Party Transactions) joined after the edit-mode verdict-masking
 * fix: their `evaluateSheetStatus` previously read a row's `forceEdited`-masked
 * display status, so an in-edit-mode error (empty required cell / Total ≠ Σ member
 * columns) was hidden from the sheet verdict while the editor showed the message.
 * The verdict now uses the unmasked `rowHasBlockingError`, so all four agree with
 * their pure validators. Each sheet is added only after dev parity is verified.
 *
 * Role 15 (Statement of Comprehensive Income), role 18 (Statement of Cash Flows —
 * Indirect Method) and role 19 (Statement of Changes in Equity) joined after the
 * global cent-diff comparison sweep — every equality/sum/cross-sheet check now
 * flags a one-cent difference (rounds to cents, compares not-equal) instead of the
 * old `> MBRS_EQUALITY_TOLERANCE` band, so the pure and mounted paths agree on the
 * boundary. Role 9 (Sub-classification) joined last — it was the special case
 * because its `BiologicalAssets` cross-sheet equality cell is user-EDITABLE (not a
 * calculated total), so it needed the same edit-mode unmask the notes sheets got:
 * `evaluateSheetStatus` now reads the component's `rowHasBlockingError()` instead
 * of the `forceEdited`-masked display status, so an in-edit-mode mismatch flips the
 * verdict to match the (never-masking) pure validator.
 *
 * Role 20 (Notes — Corporate Information) joined to make its `requiredIfCrossSheet`
 * gate refresh LIVE. Its two explanation fields become required when scope-of-filing's
 * "reclassification…changed" / "changed duration of reporting period" fields are "Yes";
 * the gate is enforced by both the mounted editor and the pure validator, but the only
 * live cross-sheet refresh hook ({@link FilingFormStateService.recomputePureVerdictStatuses})
 * iterates this set — so an UNMOUNTED role20 never flipped its dropdown tick during a
 * live scope edit (the user sits on scope-of-filing; role20 is not mounted) until it was
 * allow-listed here. Parity is clean: role20's verdict is required-field-only (static
 * required + cross-sheet required), the schemas declare no NonNegativeInteger/min
 * controls, and the pure validator's required logic matches the editor's `getValidators`.
 */
export const PURE_VERDICT_SHEETS: ReadonlySet<keyof FilingFormState> = new Set<
  keyof FilingFormState
>([
  SHEET_KEYS.statementOfFinancialPosition,
  SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity,
  SHEET_KEYS.statementOfProfitOrLoss,
  SHEET_KEYS.analysisOfProfitOrLoss,
  SHEET_KEYS.statementOfComprehensiveIncome,
  SHEET_KEYS.statementOfCashFlowsIndirectMethod,
  SHEET_KEYS.statementOfChangesInEquity,
  SHEET_KEYS.notesCorporateInformation,
  SHEET_KEYS.notesIssuedCapital,
  SHEET_KEYS.notesRelatedPartyTransactions,
]);

/**
 * The pure `hasErrors` verdict to publish for an allow-listed sheet — the
 * reusable heart of the "reuse the pure boolean" trim — or `undefined` when the
 * sheet is not on {@link PURE_VERDICT_SHEETS} (or has no registered validator),
 * in which case the caller keeps the component's display-derived boolean.
 *
 * The component keeps owning its display surface (red borders, per-row status,
 * messages); only the rolled-up sheet-level boolean is delegated, so the editor's
 * sheet-selector tick can never disagree with the headless listing for the same
 * data. Pure and Angular-free: the caller supplies the already-assembled
 * `sheetData` and `context` (the same inputs it feeds the parity check), so the
 * published verdict and the parity computation are identical by construction.
 */
export function pureVerdictForAllowListedSheet(
  validator: SheetValidator | undefined,
  sheetData: Record<string, any>,
  context: SheetValidationContext
): boolean | undefined {
  return pureStatusForAllowListedSheet(validator, sheetData, context)?.hasErrors;
}

/**
 * The full pure status — both the blocking `hasErrors` verdict AND the
 * non-blocking calc-diff `hasSuggestedChanges` (the "Validated*" / "Success *"
 * red asterisk) — to publish for an allow-listed sheet, or `undefined` when the
 * sheet is not on {@link PURE_VERDICT_SHEETS} (or has no registered validator),
 * in which case the caller keeps the component's display-derived status.
 *
 * Companion to {@link pureVerdictForAllowListedSheet}, which exposes only the
 * boolean verdict. This pair closes the **asterisk-lag** bug: the editor's
 * `updateSheetLevelStatus` previously published only the pure `hasErrors`, so a
 * monetary sheet's `hasSuggestedChanges` was frozen to whatever the one-shot
 * load-time validation cycle (`runBackgroundValidationCycle`) computed and could
 * not track an in-session edit until the next reload — an edit's "Success *"
 * only landed on the NEXT submit. The component never emits `hasSuggestedChanges`
 * for these calc-diff sheets, and they are deliberately absent from the
 * schema-warning registry, so this was their only wiring.
 *
 * Publishing BOTH flags from the SAME validator the headless listing path uses
 * (`ConversionValidatorService.runRegistry` reads `result.hasSuggestedChanges`)
 * makes the editor's submitted status identical to the headless verdict for the
 * same `sheetData` + `extractedBaseline` — the parity the pure-verdict
 * convergence is built to guarantee.
 */
export function pureStatusForAllowListedSheet(
  validator: SheetValidator | undefined,
  sheetData: Record<string, any>,
  context: SheetValidationContext
): { hasErrors: boolean; hasSuggestedChanges: boolean } | undefined {
  if (!validator || !PURE_VERDICT_SHEETS.has(validator.sheetKey)) return undefined;
  const result = validator.validate(sheetData, context);
  return {
    hasErrors: result.hasErrors,
    hasSuggestedChanges: result.hasSuggestedChanges,
  };
}
